// Field PWA foreground-time tracking (card #4838).
//
// Logs how long the app is actually on screen (Page Visibility), broken down
// by the area of the app the Tasker was in, for battery-drain analysis.
// Pure accumulation logic lives in lib/foreground_time_core.js (no DOM, no
// storage, no logging -- fully unit-testable under plain Node). This module
// is the thin browser shell around it: the real visibilitychange / pagehide /
// pageshow / freeze / resume listeners, the 60s localStorage checkpoint, the
// Turbo-route -> area mapping, and the "Foreground time" event.
//
// Usage: call initForegroundTime() once (app/javascript/application.js,
// alongside the other page-wide init calls). Everything else here reacts to
// browser events on its own.
//
// Other modules read the running totals via foregroundTimeStamp() to attach
// fgDayMs/fgDaySegments/fgVisibleNow to their own events (battery_monitor.js,
// field_battery_controller.js, field_sync_controller.js), call
// logForegroundShiftEnd() at shift end, and call pushForegroundArea() /
// popForegroundArea() while an in-app camera or scanner sheet is open
// (field_photo_camera_controller.js, field_barcode_scanner_controller.js,
// field_ranging_scanner_controller.js) since those are overlays, not routes.

import {
  areaForPath,
  flush,
  loadCheckpoint,
  onHidden,
  onVisible,
  persistable,
  shouldEmitForegroundEvent,
  snapshot,
  CHECKPOINT_INTERVAL_MS
} from "./lib/foreground_time_core"
import { logEvent } from "./field_logger"

export const FG_CHECKPOINT_KEY = "field:fg_checkpoint"
export const FG_EVENT_LAST_AT_KEY = "field:fg_event_last_at"

// Module-scoped so a Turbo visit that replaces the body (and never re-runs
// application.js) keeps accumulating against the same state -- same pattern
// as battery_monitor.js's module-level variables.
let state = null
let checkpointTimer = null
let initialised = false
let areaOverrideStack = []

export function initForegroundTime() {
  if (initialised) return
  initialised = true

  const now = Date.now()
  state = loadCheckpoint(_readCheckpoint(), now)

  if (document.visibilityState === "visible") {
    _becomeVisible()
  }

  document.addEventListener("visibilitychange", _onVisibilityChange)
  // pagehide: real unload or a bfcache freeze on some browsers. freeze/resume:
  // the explicit Page Lifecycle API events (mainly Android Chrome) for when a
  // backgrounded tab is frozen/woken without a full teardown.
  window.addEventListener("pagehide", _onPageHide)
  window.addEventListener("pageshow", _onPageShow)
  document.addEventListener("freeze", _onFreeze)
  document.addEventListener("resume", _onResume)
  // Route changes update the area without affecting visible/hidden state.
  document.addEventListener("turbo:load", _onRouteChanged)
}

// Current totals, safe to call before init (returns zeros) -- other modules
// spread this into their own log details.
export function foregroundTimeStamp() {
  if (!state) return { fgDayMs: 0, fgDaySegments: 0, fgVisibleNow: false }
  const snap = snapshot(state)
  return { fgDayMs: snap.fgDayMs, fgDaySegments: snap.fgDaySegments, fgVisibleNow: snap.fgVisibleNow }
}

// Forces the "Foreground time" event regardless of the 10 min throttle --
// called at shift end (field_battery_controller.js#logShiftEnd).
export function logForegroundShiftEnd() {
  _maybeEmitForegroundEvent({ force: true })
}

// Push/pop an area override while an in-app camera or scanner sheet is open.
// These are overlays on top of whatever route is current, not routes
// themselves, so they can't be derived from turbo:load. Stack-based so a
// sheet opened from within another overlay (not expected today, but cheap to
// get right) still restores the correct previous area on close.
export function pushForegroundArea(area) {
  areaOverrideStack.push(area)
  _setArea(area)
}

export function popForegroundArea() {
  areaOverrideStack.pop()
  const restored = areaOverrideStack[areaOverrideStack.length - 1] || areaForPath(window.location.pathname)
  _setArea(restored)
}

// -- private --

function _becomeVisible() {
  state = onVisible(state, { perfMs: performance.now(), wallMs: Date.now(), area: areaForPath(window.location.pathname) })
  _startChecking()
}

function _becomeHidden() {
  state = onHidden(state, { perfMs: performance.now(), wallMs: Date.now() })
  _stopChecking()
  _persist()
  _maybeEmitForegroundEvent({ force: false })
}

function _onVisibilityChange() {
  if (document.visibilityState === "visible") {
    _becomeVisible()
  } else {
    _becomeHidden()
  }
}

function _onPageHide() {
  // Safe even if visibilitychange already fired (onHidden/closeSegment is a
  // no-op when not visible) -- covers the odd browser that fires pagehide
  // without a preceding visibilitychange.
  _becomeHidden()
}

function _onFreeze() {
  _becomeHidden()
}

function _onPageShow(event) {
  // A plain (non-bfcache) load is already handled by initForegroundTime()
  // running again on the fresh page. event.persisted means the page is being
  // restored from the back/forward cache without re-running module code, so
  // this is the only signal we get that it's alive again.
  if (event.persisted && document.visibilityState === "visible") _becomeVisible()
}

function _onResume() {
  if (document.visibilityState === "visible") _becomeVisible()
}

function _onRouteChanged() {
  if (areaOverrideStack.length > 0) return // an overlay (camera/scanner) owns the area right now
  _setArea(areaForPath(window.location.pathname))
}

function _setArea(area) {
  if (!state) return
  state = flush(state, { perfMs: performance.now(), wallMs: Date.now(), area })
  _persist()
}

function _startChecking() {
  _stopChecking()
  // No timer while hidden (note 2) -- this only ever runs while visible, and
  // _stopChecking() tears it down the instant we go hidden.
  checkpointTimer = setInterval(_checkpointTick, CHECKPOINT_INTERVAL_MS)
}

function _stopChecking() {
  if (checkpointTimer) clearInterval(checkpointTimer)
  checkpointTimer = null
}

function _checkpointTick() {
  state = flush(state, { perfMs: performance.now(), wallMs: Date.now() })
  _persist()
}

function _persist() {
  try {
    localStorage.setItem(FG_CHECKPOINT_KEY, JSON.stringify(persistable(state)))
  } catch {
    // Storage blocked/full: the in-memory total for this session is still
    // correct, it just won't survive a kill. Never block on this.
  }
}

function _readCheckpoint() {
  try {
    const raw = localStorage.getItem(FG_CHECKPOINT_KEY)
    return raw ? JSON.parse(raw) : null
  } catch {
    return null
  }
}

function _maybeEmitForegroundEvent({ force }) {
  if (!state) return
  const lastAt = _readLastEventAt()
  const now = Date.now()
  if (!shouldEmitForegroundEvent(lastAt, now, { force })) return

  _writeLastEventAt(now)
  const snap = snapshot(state)
  logEvent("foreground", "Foreground time", {
    fgDayMs: snap.fgDayMs,
    fgDaySegments: snap.fgDaySegments,
    fgVisibleNow: snap.fgVisibleNow,
    fgByArea: snap.fgByArea
  }).catch(() => {})
}

function _readLastEventAt() {
  try {
    const stored = Number(localStorage.getItem(FG_EVENT_LAST_AT_KEY))
    return stored > 0 ? stored : null
  } catch {
    return null
  }
}

function _writeLastEventAt(timestamp) {
  try {
    localStorage.setItem(FG_EVENT_LAST_AT_KEY, String(timestamp))
  } catch {
    // Storage blocked: worst case the next background logs one event early.
  }
}
